import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { combineLatest } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrwdOrgSwitcherOrganization, CrwdOrgTab, CrwdOrgTabs, CrwdOrgTabsLabels } from '@crewdle/ui-kit';

import { OrgSwitcherService } from 'src/app/services/org-switcher/org-switcher.service';
import { OrgTabsService } from 'src/app/services/org-tabs/org-tabs.service';
import { OrgUnreadService } from 'src/app/services/org-unread/org-unread.service';
import { SidebarNavService } from 'src/app/services/sidebar-nav/sidebar-nav.service';
import { SidebarListsService } from 'src/app/services/sidebar-lists/sidebar-lists.service';
import { TeamsService } from 'src/app/services/teams/teams.service';
import { ChatSidebarService } from 'src/app/services/chat-sidebar/chat-sidebar.service';

/**
 * Connect's host for the kit's `crwd-org-tabs` — the organizations you have
 * open, at the top of the app (replaces the rail's org switcher, Vincent
 * 2026-10-21). State lives in OrgTabsService; the "+" lists every org from
 * OrgSwitcherService's cached directory. Each tab carries its org's unread
 * count (OrgUnreadService — Vincent, 2026-10-22) when it is not the tab you
 * are in. Several tabs may share an org; each is named after its page, from
 * the rail's own lists (agents, projects, spaces, chats, topics), and tabs
 * reorder by drag and drop / Alt+arrows.
 *
 * One organization: the strip still shows, and "+" opens another tab of it at
 * once — no org picker with a single choice (Vincent, 2026-10-30).
 */
@Component({
  selector: 'app-org-tabs',
  standalone: true,
  imports: [CrwdOrgTabs, TranslateModule],
  template: `
    <crwd-org-tabs
      [tabs]="tabsWithCounts()"
      [activeId]="orgTabs.activeId()"
      [organizations]="organizations()"
      [recentIds]="switcher.recentIds()"
      [loading]="switcher.loading()"
      [labels]="labels()"
      [singleOrg]="singleOrg"
      (tabSelected)="onSelect($event)"
      (tabClosed)="orgTabs.close($event)"
      (tabMoved)="orgTabs.move($event.id, $event.toIndex)"
      (orgOpened)="onOpen($event)"
      (addOpened)="onAddOpened()"
      (newTabRequested)="onNewTab()" />
  `,
  styles: `
    :host { display: block; background: var(--background-color); }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OrgTabsComponent implements OnInit {
  public readonly orgTabs = inject(OrgTabsService);
  public readonly switcher = inject(OrgSwitcherService);
  private readonly nav = inject(SidebarNavService);
  private readonly translate = inject(TranslateService);
  private readonly orgUnread = inject(OrgUnreadService);
  private readonly lists = inject(SidebarListsService);
  private readonly teams = inject(TeamsService);
  private readonly chats = inject(ChatSidebarService);
  private readonly destroyRef = inject(DestroyRef);

  /** The strip with each company's unread messages on its tabs — except the tab you are in. */
  public readonly tabsWithCounts = computed<CrwdOrgTab[]>(() => {
    const active = this.orgTabs.activeId();
    return this.orgTabs.tabs().map(({ orgId, ...tab }) =>
      (tab.id === active ? tab : { ...tab, count: this.orgUnread.countFor(orgId) }));
  });

  ngOnInit(): void {
    void this.switcher.start();
    // Page names for the tab you are in come from what the rail already lists.
    const sub = combineLatest([this.lists.agents$, this.lists.spaces$, this.lists.sharedAgents$, this.teams.teams$, this.chats.roster$])
      .subscribe(([agents, spaces, shared, teams, roster]) => {
        const conversations = new Map<string, string>();
        for (const s of spaces ?? []) if (s.conversationId && s.name) conversations.set(s.conversationId, s.name);
        for (const t of teams ?? []) if (t.conversationId && t.name) conversations.set(t.conversationId, t.name);
        for (const a of shared ?? []) if (a.conversationId && a.name) conversations.set(a.conversationId, a.name);
        for (const a of agents ?? []) if (a.conversationId && a.name) conversations.set(a.conversationId, a.name);
        const chats = new Map<string, string>();
        const topics = new Map<string, string>();
        for (const row of roster ?? []) {
          if (row.chat?.chatId && row.chat.title) chats.set(row.chat.chatId, row.chat.title);
          if (row.folder?.folderId && row.folder.name) topics.set(row.folder.folderId, row.folder.name);
        }
        this.orgTabs.setPageNames({ conversations, chats, topics });
      });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  public organizations(): CrwdOrgSwitcherOrganization[] {
    return this.switcher.directory().map((org) => ({ id: org.id, name: org.name, ...(org.logoUrl ? { avatarSrc: org.logoUrl } : {}) }));
  }

  public labels(): Partial<CrwdOrgTabsLabels> {
    const t = (key: string) => this.translate.instant(`org-tabs.${key}`);
    return {
      nav: t('nav'), add: t('add'), close: t('close'), searchPlaceholder: t('search'),
      recent: t('recent'), all: t('all'), noMatch: t('no-match'), loading: t('loading'), unread: t('unread'),
      reorderHint: t('reorder-hint'), moved: t('moved'), newTabHere: t('new-tab-here'),
    };
  }

  public onSelect(orgId: string): void {
    this.nav.closeMobile();
    this.orgTabs.select(orgId);
  }

  public onOpen(org: CrwdOrgSwitcherOrganization): void {
    this.nav.closeMobile();
    this.orgTabs.open(org.id);
  }

  /** Nothing to pick from: the person belongs to one organization. */
  public get singleOrg(): boolean {
    return !this.nav.multiOrg;
  }

  /** "+" with one organization: another tab of the org you are in. */
  public onNewTab(): void {
    const orgId = this.switcher.currentOrgId();
    if (!orgId) return;
    this.nav.closeMobile();
    this.orgTabs.open(orgId);
  }

  public onAddOpened(): void {
    // Cheap: cached names are already listed; this only fetches memberships added since boot.
    void this.switcher.refresh({ onlyMissing: true });
  }
}
